import {useEffect, useState} from 'react';
import {useDelayedFlag} from '@chekinapp/ui';
import {STATUSES} from '@chekin/core';
import {useChekinSDK} from 'context/ChekinSDK';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {useInstantCheckInOffer} from 'pages/InstantCheckIn/useInstantCheckInOffer';
import {useFirstScreenStore} from 'utils/firstScreen';
import {
  type BookingFoundHandoff,
  useBookingFoundHandoffStore,
} from '../useBookingFoundHandoffStore';
import {REDIRECT_DELAY_MS} from '../useBookingFoundRedirect';
import {BookingFoundScreen} from './BookingFoundScreen';

export const BOOKING_FOUND_TIMING = {
  minHold: REDIRECT_DELAY_MS,
  maxHold: 15000,
  leave: 350,
};

function HandoffScreen({
  handoff,
  onDone,
}: {
  handoff: BookingFoundHandoff;
  onDone: () => void;
}) {
  const {isSuccessSDK, isAuthInitializationError} = useChekinSDK();
  const {data: reservation, isLoading: isReservationLoading} = useReservation();
  const {summaryStatus} = useSummary();
  const offer = useInstantCheckInOffer();
  const screenReadyAt = useFirstScreenStore(state => state.readyAt);
  const isHeldLongEnough = useDelayedFlag(true, BOOKING_FOUND_TIMING.minHold);
  const isTimedOut = useDelayedFlag(true, BOOKING_FOUND_TIMING.maxHold);
  const [isLeaving, setIsLeaving] = useState(false);

  const isSessionReady =
    isSuccessSDK &&
    reservation?.id === handoff.reservation.id &&
    !isReservationLoading &&
    summaryStatus === STATUSES.success &&
    offer.isDecided;
  const isScreenReady = screenReadyAt > handoff.startedAt;
  const isFailed = isAuthInitializationError || summaryStatus === STATUSES.error;
  const shouldLeave =
    isFailed || isTimedOut || (isHeldLongEnough && isSessionReady && isScreenReady);

  useEffect(
    function leaveOnceTheBookingIsReady() {
      if (!shouldLeave) {
        return;
      }

      setIsLeaving(true);
      const timeout = setTimeout(onDone, BOOKING_FOUND_TIMING.leave);
      return () => clearTimeout(timeout);
    },
    [shouldLeave, onDone],
  );

  return (
    <BookingFoundScreen
      reservation={handoff.reservation}
      guestName={handoff.guestName}
      propertyName={handoff.propertyName}
      isNew={handoff.isNew}
      isLeaving={isLeaving}
    />
  );
}

function BookingFoundOverlay() {
  const handoff = useBookingFoundHandoffStore(state => state.handoff);
  const close = useBookingFoundHandoffStore(state => state.close);

  if (!handoff) {
    return null;
  }

  return <HandoffScreen key={handoff.startedAt} handoff={handoff} onDone={close} />;
}

export {BookingFoundOverlay};
